import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/api";
import { ArrowRight, Check, Plug, Upload, Code2, Clock } from "lucide-react";

// Honest categorisation. "Live" = usable today. "Connect" = build present, bring your
// own credentials. "Planned / Coming soon" = on the roadmap, not yet available.
const GROUPS = [
  {
    key: "live", title: "Live now", icon: Check,
    note: "Available on every plan today.",
    badge: { label: "Live", cls: "bg-[#1E9E6A]/10 text-[#1E9E6A] border-[#1E9E6A]/25" },
    items: [
      { name: "Native Time Tracker", desc: "Built-in timer and manual logging — the source of truth for every metric." },
      { name: "CSV import", desc: "Import time entries, staff costs and P&L lines from a spreadsheet." },
      { name: "Inbound API", desc: "Push records into NetLift with a workspace API key (X-API-Key)." },
    ],
  },
  {
    key: "connect", title: "Connect with your credentials", icon: Plug,
    note: "Working connectors — add your own API credentials in the Integration Hub.",
    badge: { label: "Available", cls: "bg-aqua/20 text-[#1d6e72] border-aqua/40" },
    items: [
      { name: "Microsoft Entra ID", desc: "Sync your employee directory (read-only) so AI usage maps to real people and teams." },
      { name: "Microsoft 365 Copilot", desc: "Import per-user Copilot adoption from Microsoft's usage reports — never prompt or document content." },
      { name: "ChatGPT Workspace", desc: "Sync workspace members with a read-only ChatGPT Admin key; import per-user adoption from the Workspace Analytics export. NetLift never collects conversation content." },
      { name: "NetSuite Finance", desc: "Connect approved P&L and AI investment data to validate actual financial impact. NetLift does not require customer, order, inventory or payment-card data." },
      { name: "Jira", desc: "Import work items to attribute time to issues and projects." },
      { name: "Azure DevOps", desc: "Sync work items for engineering teams." },
      { name: "OpenAI usage & cost", desc: "Pull API usage and spend for cost attribution." },
    ],
  },
  {
    key: "beta", title: "In beta", icon: Code2,
    note: "Early access — request access for your workspace.",
    badge: { label: "Beta", cls: "bg-editorial/10 text-editorial border-editorial/30" },
    items: [
      { name: "Okta, Auth0", desc: "SSO / SCIM identity provisioning." },
      { name: "Google Workspace", desc: "Directory sync and sign-in." },
      { name: "Anthropic / Claude usage", desc: "AI spend import for Claude." },
    ],
  },
  {
    key: "planned", title: "Planned", icon: Upload,
    note: "On the roadmap. Not yet available — no false promises.",
    badge: { label: "Planned", cls: "bg-gold/20 text-[#8a6d12] border-gold/50" },
    items: [
      { name: "Work & support", desc: "Zendesk, ServiceNow, GitHub Issues, Linear, Asana, Monday, Harvest." },
      { name: "AI usage", desc: "Azure OpenAI, GitHub Copilot metrics, Gemini." },
      { name: "Finance", desc: "QuickBooks, Xero, Sage Intacct." },
    ],
  },
  {
    key: "soon", title: "Coming soon", icon: Clock,
    note: "In active development.",
    badge: { label: "Coming soon", cls: "bg-slate-100 text-slate-600 border-slate-200" },
    items: [
      { name: "NetLift Desktop Timer", desc: "Lightweight desktop companion with offline tracking — privacy-first, no hidden tracking." },
      { name: "Nightly auto-sync", desc: "Scheduled connector syncs so data stays current automatically." },
    ],
  },
];

export default function Integrations() {
  const navigate = useNavigate();
  useEffect(() => {
    track("integrations_page_viewed");
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="border-b border-ink/10 bg-[#FFF8E9]">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <span className="inline-flex items-center rounded-full border border-editorial/20 bg-editorial/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-editorial">
            Integrations &amp; API
          </span>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-semibold leading-[1.08] tracking-tight text-ink sm:text-5xl">
            Bring your work in. Keep your evidence in one place.
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
            Track time natively, import from a spreadsheet, push records over our API, or connect a tool
            with your own credentials. Everything flows through the same AI-value process — so your
            dashboards, decisions and forecasts stay consistent.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" className="bg-editorial hover:bg-editorial/90" onClick={() => { track("primary_cta_clicked", { from: "integrations" }); navigate("/signup"); }} data-testid="integrations-cta">
              Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" className="border-ink/20" onClick={() => navigate("/contact")} data-testid="integrations-contact">
              Ask about a connector
            </Button>
          </div>
          <p className="mt-4 text-sm text-slate-500">We label what is live versus planned. No connector is shown as working until it is.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="space-y-12">
          {GROUPS.map((g) => (
            <div key={g.key} data-testid={`integrations-group-${g.key}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-ink text-white">
                  <g.icon className="h-4.5 w-4.5" />
                </span>
                <h2 className="font-heading text-2xl font-semibold tracking-tight text-ink">{g.title}</h2>
                <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${g.badge.cls}`}>{g.badge.label}</span>
              </div>
              <p className="mt-2 text-sm text-slate-500">{g.note}</p>
              <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {g.items.map((it) => (
                  <div key={it.name} className="rounded-xl border border-ink/10 bg-white p-5">
                    <p className="font-heading text-base font-semibold text-ink">{it.name}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{it.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 rounded-2xl border border-ink/10 bg-white p-8">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <h3 className="font-heading text-2xl font-semibold text-ink">A simple API for pushing records</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Generate a workspace API key and send time or staff-cost records straight into NetLift.
                Imported records run through the same baseline, cost and decision pipeline as everything else.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                {["Per-workspace keys you can rotate", "Records appear in the Time Tracker review queue",
                  "Same evidence-quality scoring as manual entry"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E9E6A]" /> {f}</li>
                ))}
              </ul>
            </div>
            <pre className="overflow-x-auto rounded-xl bg-ink p-5 text-xs leading-relaxed text-aqua" data-testid="integrations-api-snippet">
{`POST /api/staff-costs/import
X-API-Key: <your-workspace-key>
Content-Type: application/json

{
  "records": [
    { "name": "Senior Developer",
      "loaded_hourly_cost": 125 }
  ]
}`}
            </pre>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
